import { useState } from "react";
import type { TenantSummary } from "./ReimbursementEditor";

/* -------------------------------------------------------------------------- */
/* C3: Bulk "Apply to All" Actions                                            */
/* -------------------------------------------------------------------------- */

const CAM_OPTIONS = [
  { value: "gross", label: "Gross" },
  { value: "NNN", label: "NNN" },
  { value: "base_year_stop", label: "Base Year Stop" },
  { value: "expense_cap", label: "Expense Cap" },
  { value: "fixed_psf", label: "Fixed PSF" },
];

interface BulkToolbarProps {
  tenants: TenantSummary[];
  selectedIds: Set<string>;
  onToggleId: (id: string) => void;
  onSelectAll: () => void;
  onDeselectAll: () => void;
  onBulkSetCamType: (camType: string, tenantIds: string[]) => void;
}

export default function BulkReimbursementToolbar({
  tenants,
  selectedIds,
  onToggleId,
  onSelectAll,
  onDeselectAll,
  onBulkSetCamType,
}: BulkToolbarProps) {
  const [bulkCamType, setBulkCamType] = useState("NNN");
  const allSelected =
    tenants.length > 0 && selectedIds.size === tenants.length;
  const someSelected = selectedIds.size > 0;

  return (
    <div className="bg-grove-50 border border-grove-200 rounded-xl px-4 py-3 flex items-center gap-3 flex-wrap">
      {/* Select all / deselect all */}
      <label className="flex items-center gap-1.5 text-xs text-bark-600">
        <input
          type="checkbox"
          checked={allSelected}
          onChange={() => (allSelected ? onDeselectAll() : onSelectAll())}
          className="rounded border-grove-300 text-forest-600 focus:ring-forest-500"
        />
        <span className="font-medium">
          {allSelected ? "Deselect All" : "Select All"}
        </span>
      </label>

      <div className="h-4 w-px bg-grove-200" />

      {/* Selected count */}
      <span className="text-[11px] text-bark-400 tabular-nums">
        {selectedIds.size} of {tenants.length} selected
      </span>

      <div className="h-4 w-px bg-grove-200" />

      {/* CAM type dropdown + apply button */}
      <div className="flex items-center gap-2">
        <label className="text-[11px] font-medium text-bark-500">
          Set CAM Type:
        </label>
        <select
          value={bulkCamType}
          onChange={(e) => setBulkCamType(e.target.value)}
          className="px-2 py-1 text-xs bg-white border border-grove-300 rounded-md text-bark-700 focus:ring-1 focus:ring-forest-500"
        >
          {CAM_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <button
          disabled={!someSelected}
          onClick={() =>
            onBulkSetCamType(bulkCamType, Array.from(selectedIds))
          }
          className="px-3 py-1 text-xs font-semibold text-white bg-forest-600 rounded-md hover:bg-forest-500 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          Apply to Selected
        </button>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Checkbox for individual tenant rows                                        */
/* -------------------------------------------------------------------------- */

export function TenantCheckbox({
  tenantId,
  checked,
  onToggle,
}: {
  tenantId: string;
  checked: boolean;
  onToggle: (id: string) => void;
}) {
  return (
    <input
      type="checkbox"
      checked={checked}
      onChange={(e) => {
        e.stopPropagation();
        onToggle(tenantId);
      }}
      onClick={(e) => e.stopPropagation()}
      className="rounded border-grove-300 text-forest-600 focus:ring-forest-500 mr-2"
    />
  );
}
